// frontend/apps/widgets/src/widgets/GiftVoucher/components/GiftVoucherFilters.js
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  Slider,
  FormControl,
  Select,
  MenuItem,
  Button,
  Collapse,
  IconButton,
  alpha,
  Chip,
  useMediaQuery,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {getContrastTextColor} from 'packages/common/utils/colorUtils';
import {getCurrencySymbol} from 'packages/common/config/currencies';
import {PRIMARY} from 'packages/common/theme/palette';
import {FlowLanguageSelector} from 'packages/common/components/flow-language-selector';

const getSortOptions = (t) => {
  if (!t) {
    return [
      {value: 'default', label: 'Prix croissant'},
      {value: 'price_desc', label: 'Prix décroissant'},
      {value: 'alpha_asc', label: 'A à Z'},
      {value: 'alpha_desc', label: 'Z à A'},
    ];
  }
  return [
    {value: 'default', label: t('common.giftVoucher.filters.sort.priceAsc')},
    {value: 'price_desc', label: t('common.giftVoucher.filters.sort.priceDesc')},
    {value: 'alpha_asc', label: t('common.giftVoucher.filters.sort.alphaAsc')},
    {value: 'alpha_desc', label: t('common.giftVoucher.filters.sort.alphaDesc')},
  ];
};

function GiftVoucherFilters({
  // Filtres de prix et tri
  priceRange,
  setPriceRange,
  maxPrice,
  sortBy,
  setSortBy,
  // Filtres de catégories
  categories = [],
  selectedCategories = [],
  setSelectedCategories,
  isCategoriesLoading = false,
  // Styles et config
  primaryColor = PRIMARY.main,
  embedded = false,
  displayMode = 'fixed',
  onReset,
  t,
  // Devise
  currencySymbol = getCurrencySymbol(),
  // Language selector
  isMultiLanguageEnabled = false,
  enabledLanguages = [],
  currentLanguage = 'fr',
  onLanguageChange,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const BREAKPOINT_WIDE_MODE = 900;
  const isWideMode = useMediaQuery(`(min-width:${BREAKPOINT_WIDE_MODE}px)`);
  const sortOptions = getSortOptions(t);

  const getSmartMarks = (max) => {
    if (max <= 200) {
      return [0, 50, 100, 150, 200].filter((val) => val <= max);
    }
    if (max <= 500) {
      return [0, 100, 250, 500].filter((val) => val <= max);
    }
    if (max <= 1000) {
      return [0, 250, 500, 750, 1000].filter((val) => val <= max);
    }
    const step = Math.ceil(max / 4 / 100) * 100;
    return Array.from({length: 5}, (_, i) => Math.min(i * step, max));
  };

  const marks = getSmartMarks(maxPrice).map((value) => ({
    value,
    label: `${value}${currencySymbol}`,
  }));

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleCategoryClick = (categoryId) => {
    if (categoryId === 'all') {
      setSelectedCategories([]);
      return;
    }

    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter((id) => id !== categoryId));
    } else {
      setSelectedCategories([...selectedCategories, categoryId]);
    }
  };

  const handleReset = () => {
    const resetRange = [0, maxPrice];
    setPriceRange(resetRange);
    setSortBy('default');
    setSelectedCategories([]);
    if (onReset) onReset();
  };

  // Composant Tri
  const SortSelect = () => (
    <FormControl fullWidth size="small">
      <Typography
        variant="body2"
        color="text.secondary"
        id="sort-select-label"
        sx={{mb: 1, fontWeight: 500}}
      >
        {t ? t('widgets.giftVoucher.filters.sortLabel') : 'Trier par'}
      </Typography>
      <Select
        value={sortBy}
        onChange={handleSortChange}
        aria-labelledby="sort-select-label"
        sx={{
          'borderRadius': '8px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(primaryColor, 0.23),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
          },
        }}
      >
        {sortOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Composant de sélection de plage de prix
  const PriceSlider = () => {
    // État local pour l'affichage temps réel du slider sans déclencher de re-render du parent
    const [localPriceRange, setLocalPriceRange] = useState(priceRange);

    const priceMin = priceRange[0];
    const priceMax = priceRange[1];

    // Synchronise l'état local quand le filtre parent change (ex: reset)
    useEffect(() => {
      setLocalPriceRange(priceRange);
    }, [priceMin, priceMax]);

    // Pattern en deux étapes pour optimiser les performances du slider
    const handleSliderChange = (_, newValue) => {
      // Mise à jour locale uniquement pendant le drag pour un affichage fluide
      setLocalPriceRange(newValue);
    };

    const handleSliderChangeCommitted = (_, newValue) => {
      // Applique le filtre au parent uniquement quand l'utilisateur relâche le slider
      setPriceRange(newValue);
    };

    return (
      <Box sx={{width: '100%'}}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography aria-hidden="true" variant="body2" color="text.secondary" sx={{fontWeight: 600, fontSize: '0.875rem'}}>{currencySymbol}</Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t ? t('widgets.giftVoucher.filters.priceLabel') : 'Prix'}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                minWidth: 100,
                textAlign: 'right',
                color: primaryColor,
              }}
            >
              {localPriceRange[0]}{currencySymbol} - {localPriceRange[1]}{currencySymbol}
            </Typography>
          </Stack>

          <Slider
            value={localPriceRange}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            min={0}
            max={maxPrice}
            step={5}
            marks={marks}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}${currencySymbol}`}
            disableSwap
            aria-label="Fourchette de prix"
            getAriaValueText={(value) => `${value} ${currencySymbol}`}
            sx={{
              '@media (max-width: 600px)': {
                '& .MuiSlider-markLabel': {
                  display: 'none',
                },
              },
              '& .MuiSlider-rail': {
                height: 4,
                backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.12),
              },
              '& .MuiSlider-track': {
                height: 4,
                backgroundColor: primaryColor,
              },
              '& .MuiSlider-thumb': {
                'width': 20,
                'height': 20,
                'backgroundColor': 'white',
                'border': `2px solid ${primaryColor}`,
                'boxShadow': `0 0 0 3px ${alpha(primaryColor, 0.16)}`,
                '&:hover': {
                  boxShadow: `0 0 0 8px ${alpha(primaryColor, 0.16)}`,
                },
                '&.Mui-active': {
                  boxShadow: `0 0 0 12px ${alpha(primaryColor, 0.24)}`,
                },
              },
              '& .MuiSlider-mark': {
                'backgroundColor': (theme) => alpha(theme.palette.grey[500], 0.16),
                'height': 4,
                '&.MuiSlider-markActive': {
                  backgroundColor: 'white',
                  opacity: 1,
                },
              },
              '& .MuiSlider-markLabel': {
                'fontSize': '0.75rem',
                'fontWeight': 500,
                'color': 'text.secondary',
                '&.MuiSlider-markLabelActive': {
                  color: 'text.primary',
                },
              },
            }}
          />
        </Stack>
      </Box>
    );
  };

  // Composant Catégories
  const CategoriesSection = () => {
    if (!categories || categories.length === 0) return null;

    return (
      <Box sx={{width: '100%'}}>
        <Typography
          variant="body2"
          color="text.secondary"
          id="categories-label"
          sx={{mb: 1.5, fontWeight: 500}}
        >
          {t ? t('widgets.giftVoucher.filters.categoriesLabel') : 'Catégories'}
        </Typography>
        <Box role="group" aria-labelledby="categories-label" sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
          <Chip
            key="all"
            label={t ? t('common.giftVoucher.filters.allCategories') : 'Toutes les catégories'}
            onClick={() => handleCategoryClick('all')}
            role="checkbox"
            aria-checked={selectedCategories.length === 0}
            size="small"
            sx={{
              'borderRadius': '12px',
              'height': 32,
              'backgroundColor': selectedCategories.length === 0 ? primaryColor : (theme) => alpha(theme.palette.grey[500], 0.08),
              'color': selectedCategories.length === 0 ? getContrastTextColor(primaryColor) : 'text.primary',
              'fontWeight': 500,
              'fontSize': '0.8rem',
              'transition': 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: selectedCategories.length === 0 ? primaryColor : (theme) => alpha(theme.palette.grey[500], 0.12),
                transform: 'translateY(-1px)',
                boxShadow: selectedCategories.length === 0 ? `0 2px 8px ${alpha(primaryColor, 0.4)}` : 'none',
              },
              ...(selectedCategories.length === 0 && {
                boxShadow: `0 2px 6px ${alpha(primaryColor, 0.24)}`,
              }),
            }}
          />

          {categories.map((category) => {
            const isSelected = selectedCategories.includes(category._id);
            return (
              <Chip
                key={category._id}
                label={category.name}
                onClick={() => handleCategoryClick(category._id)}
                role="checkbox"
                aria-checked={isSelected}
                size="small"
                sx={{
                  'borderRadius': '12px',
                  'height': 32,
                  'backgroundColor': isSelected ? primaryColor : (theme) => alpha(theme.palette.grey[500], 0.08),
                  'color': isSelected ? getContrastTextColor(primaryColor) : 'text.primary',
                  'fontWeight': 500,
                  'fontSize': '0.8rem',
                  'transition': 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: isSelected ? primaryColor : (theme) => alpha(theme.palette.grey[500], 0.12),
                    transform: 'translateY(-1px)',
                    boxShadow: isSelected ? `0 2px 8px ${alpha(primaryColor, 0.4)}` : 'none',
                  },
                  ...(isSelected && {
                    boxShadow: `0 2px 6px ${alpha(primaryColor, 0.24)}`,
                  }),
                }}
              />
            );
          })}
        </Box>
      </Box>
    );
  };

  // Bouton de réinitialisation des filtres actifs
  const ResetButton = () => {
    // Détecte si au moins un filtre est actif pour afficher le bouton
    const hasActiveFilters =
      priceRange[0] > 0 ||
      priceRange[1] < maxPrice ||
      sortBy !== 'default' ||
      selectedCategories.length > 0;

    if (!hasActiveFilters) return null;

    return (
      <Button
        variant="text"
        size="small"
        onClick={handleReset}
        startIcon={<RestartAltIcon aria-hidden="true" />}
        sx={{
          'color': 'text.secondary',
          'fontSize': '0.8rem',
          'fontWeight': 500,
          'px': 1.5,
          'alignSelf': isWideMode ? 'center' : 'flex-start',
          '&:hover': {
            color: primaryColor,
            backgroundColor: alpha(primaryColor, 0.08),
          },
        }}
      >
        {t ? t('widgets.giftVoucher.filters.reset') : 'Réinitialiser'}
      </Button>
    );
  };

  // Indicateur pour le badge du bouton de filtres en mode mobile
  const hasActiveFilters =
    priceRange[0] > 0 ||
    priceRange[1] < maxPrice ||
    sortBy !== 'default' ||
    selectedCategories.length > 0;

  // MODE LARGE - Layout horizontal
  if (isWideMode) {
    return (
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'white',
          borderRadius: 2,
          p: 3,
          boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        <Stack spacing={3}>
          {/* Header avec titre et reset */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              pb: 2,
              borderBottom: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TuneIcon sx={{color: primaryColor, fontSize: '1.3rem'}} aria-hidden="true" />
              <Typography variant="h6" fontWeight={600}>
                {t ? t('widgets.giftVoucher.filters.title') : 'Filtres et tri'}
              </Typography>
              {hasActiveFilters && (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: primaryColor,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  !
                </Box>
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Sélecteur de langue en mode fixed */}
              {displayMode === 'fixed' && isMultiLanguageEnabled && enabledLanguages.length > 0 && (
                <FlowLanguageSelector
                  currentLanguage={currentLanguage}
                  availableLanguages={enabledLanguages}
                  onLanguageChange={onLanguageChange}
                  ariaLabel={t ? t('widgets.languageSelector.ariaLabel') : 'Changer la langue'}
                />
              )}
              <ResetButton />
            </Stack>
          </Stack>

          {/* Filtres en layout horizontal */}
          <Stack
            direction="row"
            spacing={4}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            {/* Section Catégories */}
            {categories && categories.length > 0 && (
              <Box sx={{flex: 1, minWidth: 0}}>
                <CategoriesSection />
              </Box>
            )}

            {/* Section Tri */}
            <Box sx={{width: '200px', flexShrink: 0}}>
              <SortSelect />
            </Box>

            {/* Section Prix */}
            <Box sx={{width: '300px', flexShrink: 0}}>
              <PriceSlider />
            </Box>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // MODE ÉTROIT - Layout avec accordéon
  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
        overflow: 'hidden',
      }}
    >
      {/* Header avec bouton expand/collapse */}
      <Box
        sx={{
          'p': 2,
          'borderBottom': (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
          'cursor': 'pointer',
          'backgroundColor': isExpanded ? alpha(primaryColor, 0.04) : 'transparent',
          'transition': 'background-color 0.2s',
          '&:hover': {
            backgroundColor: alpha(primaryColor, 0.08),
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <TuneIcon sx={{color: primaryColor, fontSize: '1.2rem'}} aria-hidden="true" />
            <Typography variant="subtitle2" fontWeight={600}>
              {t ? t('widgets.giftVoucher.filters.title') : 'Filtres et tri'}
            </Typography>
            {hasActiveFilters && (
              <Box
                sx={{
                  minWidth: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: primaryColor,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                !
              </Box>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {/* Sélecteur de langue en mode fixed */}
            {displayMode === 'fixed' && isMultiLanguageEnabled && enabledLanguages.length > 0 && (
              <Box onClick={(e) => e.stopPropagation()}>
                <FlowLanguageSelector
                  currentLanguage={currentLanguage}
                  availableLanguages={enabledLanguages}
                  onLanguageChange={onLanguageChange}
                  ariaLabel={t ? t('widgets.languageSelector.ariaLabel') : 'Changer la langue'}
                />
              </Box>
            )}
            <IconButton
              size="small"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Réduire les filtres' : 'Afficher les filtres'}
              sx={{color: primaryColor}}
            >
              {isExpanded ? <ExpandLessIcon aria-hidden="true" /> : <ExpandMoreIcon aria-hidden="true" />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Contenu des filtres */}
      <Collapse in={isExpanded}>
        <Box sx={{p: 3}}>
          <Stack spacing={3}>
            {/* Section Catégories */}
            <CategoriesSection />

            {/* Section Tri */}
            <SortSelect />

            {/* Section Prix */}
            <PriceSlider />

            {/* Bouton Reset */}
            <ResetButton />
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

GiftVoucherFilters.propTypes = {
  // Filtres de prix et tri
  priceRange: PropTypes.arrayOf(PropTypes.number).isRequired,
  setPriceRange: PropTypes.func.isRequired,
  maxPrice: PropTypes.number.isRequired,
  sortBy: PropTypes.string.isRequired,
  setSortBy: PropTypes.func.isRequired,
  // Filtres de catégories
  categories: PropTypes.array,
  selectedCategories: PropTypes.array,
  setSelectedCategories: PropTypes.func.isRequired,
  isCategoriesLoading: PropTypes.bool,
  // Styles et config
  primaryColor: PropTypes.string,
  embedded: PropTypes.bool,
  displayMode: PropTypes.string,
  onReset: PropTypes.func,
  t: PropTypes.func,
  currencySymbol: PropTypes.string,
  // Language selector
  isMultiLanguageEnabled: PropTypes.bool,
  enabledLanguages: PropTypes.array,
  currentLanguage: PropTypes.string,
  onLanguageChange: PropTypes.func,
};

export default GiftVoucherFilters;
